<template>
	<div>
		<avatar :src="props.thumbnailSrc ?? props.src" class="cursor-pointer flex items-center min-h-96" :size="props.size" color="transparent" @click="handleAvatarClick" :round="props.round" immediate>
			<overlay v-if="isLoading || $slots.overlay" :model-value="true" absolute center :opacity="0.7">
				<loading-icon v-if="isLoading" color="white"/>
				<slot v-else name="overlay"/>
			</overlay>
		</avatar>

		<client-only>
			<lazy-image-modal v-if="props.src" v-model="isActive" :src="[{path: props.src}]">
				<span></span>
				<template #button>
					<btn v-if="props.editable" @click="editAvatar" :loading="isLoading">
						<icon :icon="icons.edit"/>
						<span>{{ t('action.edit') }}</span>
					</btn>
				</template>
			</lazy-image-modal>
		</client-only>

		<client-only v-if="editable">
			<lazy-image-cropper-modal v-model="isEditModalActive" :title="props.editTitle ?? t('action.edit_avatar')" :action="props.action"
									  :src="props.src"
									  :thumbnail="props.src ? props.thumbnailSrc : null" help-size="500x500px" help-ratio="1/1"
									  :name="props.name"
									  :with-thumbnail="!props.withoutThumbnail"
									  @success="avatarUpdated" :avatar="props.round" deletable/>

		</client-only>
	</div>
</template>

<script setup lang="ts">
import {editIcon} from "@spoferan/nuxt-spoferan/icons";
import {NativeApp} from "@spoferan/nuxt-spoferan/services";
import type {Image} from "@spoferan/spoferan-ts-core";

const props = withDefaults(defineProps<{
	src: Image | null,
	thumbnailSrc?: Image | null,

	// Whether the click actions can only be triggered by code and not by user actions
	passive?: boolean,

	// Whether the avatar can be changed
	editable?: boolean,

	// Whether a full border radius shall be applied
	round?: boolean,

	size?: number,

	// The API EP URL to send the update request to
	action?: string,

	// The prop name of the image to send to the API
	name?: string,

	editTitle?: string,

	// Whether no explicit thumbnail image shall be uploaded
	withoutThumbnail?: boolean,
}>(), {
	size: 112,
	name: 'avatar'
});
const emit = defineEmits(['updated']);

const {$auth} = useNuxtApp();
const {t} = useI18n();

const isActive = defineModel();
const isEditModalActive = ref(false);
const isLoading = ref(false);

const icons = {
	edit: editIcon,
};

function avatarUpdated(response) {
	isActive.value = false;
	isEditModalActive.value = false;
	emit('updated', response)
}

function handleAvatarClick() {
	if (props.passive) {
		return;
	}
	if (props.src) {
		isActive.value = true;
	} else if (props.editable) {
		editAvatar();
	}
}

async function editAvatar() {
	if (!props.action) {
		throw new Error('No action provided for avatar upload');
	}

	isActive.value = false;

	if (NativeApp.isNativeApp()) {
		await NativeApp.uploadImage({
			crop: true,
			ratio: '1/1',
			action: props.action as string,
			method: 'PUT',
			name: props.name,
			auth: $auth.bearerToken as string
		}, response => {
			isActive.value = false
			isLoading.value = false;
			if (response) {
				emit('updated', response)
			}
		}, loading => {
			isActive.value = false
			isLoading.value = loading;
		});
	} else {
		isEditModalActive.value = true;
	}
}

defineExpose({
	editAvatar
});
</script>